import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import { Capacitor } from '@capacitor/core';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import './Auth.css';

// On native iOS we replace the Google web button with the Apple Sign-In
// plugin button. App Store guideline 4.8 requires Apple alongside any other
// social sign-in, and Apple is the natively-supported flow on iOS.
const IS_NATIVE = Capacitor.isNativePlatform();

const Signup = ({ onSwitchToLogin, onSuccess }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { signup, loginWithGoogle, loginWithApple } = useAuth();

  const handleAppleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await SignInWithApple.authorize({
        clientId: 'com.ranga.app',
        redirectURI: 'https://www.ranga.com/auth/apple/callback',
        scopes: 'email name',
      });
      const { identityToken, givenName, familyName } = res.response || {};
      if (!identityToken) {
        setError('Apple Sign-In did not return a token.');
        setLoading(false);
        return;
      }
      const result = await loginWithApple({ identityToken, givenName, familyName });
      setLoading(false);
      if (result.success) {
        if (onSuccess) onSuccess(result.user);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setLoading(false);
      if (err && /canceled|cancel/i.test(err.message || '')) return;
      console.error('Apple Sign-In error:', err);
      setError('Apple Sign-In failed. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    const result = await signup(username, email, password, displayName);
    
    setLoading(false);

    if (result.success) {
      console.log('Signup successful:', result.user);
      if (onSuccess) {
        onSuccess(result.user);
      }
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 className="auth-title">Join Ranga</h1>
        <h2 className="auth-subtitle">Create your account</h2>
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="username">Username*</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Choose a unique username"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email*</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="displayName">Display Name (Optional)</label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="How others will see you"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password*</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password*</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              required
              disabled={loading}
            />
          </div>

          {error && <div className="auth-error">{error}</div>}

          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading ? 'Creating account...' : 'Sign Up'}
          </button>
        </form>

        {IS_NATIVE && (
          <>
            <div className="auth-divider">
              <span>OR</span>
            </div>
            <button
              type="button"
              className="apple-signin-button"
              onClick={handleAppleSignIn}
              disabled={loading}
            >
              <span aria-hidden="true" className="apple-signin-icon"></span>
              Sign up with Apple
            </button>
          </>
        )}

        {!IS_NATIVE && (
          <>
            <div className="auth-divider">
              <span>OR</span>
            </div>

            <div className="google-login-wrapper">
              <GoogleLogin
                onSuccess={async (credentialResponse) => {
                  setLoading(true);
                  const result = await loginWithGoogle(credentialResponse.credential);
                  setLoading(false);

                  if (result.success) {
                    console.log('Google signup successful:', result.user);
                    if (onSuccess) {
                      onSuccess(result.user);
                    }
                  } else {
                    setError(result.error);
                  }
                }}
                onError={() => {
                  setError('Google Sign-Up failed. Please try again.');
                }}
                useOneTap={false}
                theme="outline"
                size="large"
                text="signup_with"
                shape="rectangular"
                logo_alignment="left"
              />
            </div>
          </>
        )}

        <div className="auth-switch">
          Already have an account? {' '}
          <button 
            onClick={onSwitchToLogin}
            className="auth-link"
            disabled={loading}
          >
            Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default Signup;
